/**
 * GameLoop - Core game loop running at 60 FPS
 * Handles timing, delta time calculation, and game loop phases
 */

export class GameLoop {
  // Loop state
  private isRunning: boolean = false;
  private isPaused: boolean = false;
  private animationFrameId: number | null = null;

  // Timing
  private targetFPS: number = 60;
  private targetFrameTime: number = 1000 / 60; // ~16.67ms
  private lastFrameTime: number = 0;
  private deltaTime: number = 0;
  private elapsedTime: number = 0;
  private accumulatedTime: number = 0;
  private timeScale: number = 1.0; // Time scale multiplier for debug speed control

  // Performance monitoring
  private frameCount: number = 0;
  private fps: number = 60;
  private fpsUpdateInterval: number = 1000; // Update FPS every 1 second
  private lastFpsUpdate: number = 0;
  private frameTimeHistory: number[] = [];
  private maxFrameTimeHistorySize: number = 60;

  // Callbacks for game loop phases
  private onInput: ((deltaTime: number) => void) | null = null;
  private onUpdate: ((deltaTime: number) => void) | null = null;
  private onRender: ((deltaTime: number) => void) | null = null;
  private onLateUpdate: ((deltaTime: number) => void) | null = null;
  private onPause: (() => void) | null = null;
  private onResume: (() => void) | null = null;

  constructor(targetFPS: number = 60) {
    this.targetFPS = targetFPS;
    this.targetFrameTime = 1000 / targetFPS;
  }

  /**
   * Starts the game loop
   */
  public start(): void {
    if (this.isRunning) {
      console.warn('GameLoop: Already running');
      return;
    }

    console.log('GameLoop: Starting game loop at', this.targetFPS, 'FPS');
    this.isRunning = true;
    this.isPaused = false;
    this.lastFrameTime = performance.now();
    this.lastFpsUpdate = this.lastFrameTime;
    this.frameCount = 0;
    this.elapsedTime = 0;

    // Start the loop
    this.loop(this.lastFrameTime);
  }

  /**
   * Stops the game loop
   */
  public stop(): void {
    if (!this.isRunning) {
      console.warn('GameLoop: Not running');
      return;
    }

    console.log('GameLoop: Stopping game loop');
    this.isRunning = false;
    this.isPaused = false;

    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }

  /**
   * Pauses the game loop
   */
  public pause(): void {
    if (!this.isRunning) {
      console.warn('GameLoop: Cannot pause - not running');
      return;
    }

    if (this.isPaused) {
      console.warn('GameLoop: Already paused');
      return;
    }

    console.log('GameLoop: Pausing game loop');
    this.isPaused = true;

    if (this.onPause) {
      this.onPause();
    }
  }

  /**
   * Resumes the game loop from pause
   */
  public resume(): void {
    if (!this.isRunning) {
      console.warn('GameLoop: Cannot resume - not running');
      return;
    }

    if (!this.isPaused) {
      console.warn('GameLoop: Not paused');
      return;
    }

    console.log('GameLoop: Resuming game loop');
    this.isPaused = false;
    this.lastFrameTime = performance.now(); // Reset to avoid large delta

    if (this.onResume) {
      this.onResume();
    }
  }

  /**
   * Main game loop - runs every frame
   */
  private loop(currentTime: number): void {
    if (!this.isRunning) {
      return;
    }

    // Request next frame
    this.animationFrameId = requestAnimationFrame((time) => this.loop(time));

    // Calculate delta time (in seconds)
    const frameTime = currentTime - this.lastFrameTime;
    this.deltaTime = frameTime / 1000; // Convert to seconds
    this.lastFrameTime = currentTime;

    // Cap delta time to prevent spiral of death
    // (if game freezes for a moment, don't try to catch up)
    if (this.deltaTime > 0.1) {
      this.deltaTime = 0.1; // Max 100ms delta
    }

    // Check for debug time scale from window global
    if (typeof window !== 'undefined' && (window as any).debugTimeScale !== undefined) {
      this.timeScale = (window as any).debugTimeScale;
    }

    // Apply time scale to delta time
    const scaledDeltaTime = this.deltaTime * this.timeScale;

    // Update elapsed time
    if (!this.isPaused) {
      this.elapsedTime += scaledDeltaTime;
    }

    // Update FPS counter
    this.updateFPS(currentTime, frameTime);

    // Skip game updates if paused (but still render)
    if (!this.isPaused) {
      // Phase 1: Input Processing (use regular delta, not scaled)
      if (this.onInput) {
        this.onInput(this.deltaTime);
      }

      // Phase 2: Update (use scaled delta time for time-based mechanics)
      if (this.onUpdate) {
        this.onUpdate(scaledDeltaTime);
      }
    }

    // Phase 3: Render (always render, even when paused)
    if (this.onRender) {
      this.onRender(this.deltaTime);
    }

    // Phase 4: Late Update (camera, UI sync, etc.)
    if (!this.isPaused && this.onLateUpdate) {
      this.onLateUpdate(scaledDeltaTime);
    }

    this.frameCount++;
  }

  /**
   * Updates FPS calculation
   */
  private updateFPS(currentTime: number, frameTime: number): void {
    // Add to frame time history
    this.frameTimeHistory.push(frameTime);
    if (this.frameTimeHistory.length > this.maxFrameTimeHistorySize) {
      this.frameTimeHistory.shift();
    }

    // Update FPS every second
    if (currentTime - this.lastFpsUpdate >= this.fpsUpdateInterval) {
      // Calculate average frame time
      const avgFrameTime =
        this.frameTimeHistory.reduce((sum, time) => sum + time, 0) /
        this.frameTimeHistory.length;

      // Calculate FPS
      this.fps = avgFrameTime > 0 ? 1000 / avgFrameTime : 60;

      this.lastFpsUpdate = currentTime;
    }
  }

  /**
   * Set callback for input processing phase
   */
  public setInputCallback(callback: (deltaTime: number) => void): void {
    this.onInput = callback;
  }

  /**
   * Set callback for update phase
   */
  public setUpdateCallback(callback: (deltaTime: number) => void): void {
    this.onUpdate = callback;
  }

  /**
   * Set callback for render phase
   */
  public setRenderCallback(callback: (deltaTime: number) => void): void {
    this.onRender = callback;
  }

  /**
   * Set callback for late update phase
   */
  public setLateUpdateCallback(callback: (deltaTime: number) => void): void {
    this.onLateUpdate = callback;
  }

  /**
   * Set callback for pause event
   */
  public setPauseCallback(callback: () => void): void {
    this.onPause = callback;
  }

  /**
   * Set callback for resume event
   */
  public setResumeCallback(callback: () => void): void {
    this.onResume = callback;
  }

  // Getters
  public getIsRunning(): boolean {
    return this.isRunning;
  }

  public getIsPaused(): boolean {
    return this.isPaused;
  }

  public getDeltaTime(): number {
    return this.deltaTime;
  }

  public getElapsedTime(): number {
    return this.elapsedTime;
  }

  public getFPS(): number {
    return Math.round(this.fps);
  }

  public getFrameCount(): number {
    return this.frameCount;
  }

  public getTargetFPS(): number {
    return this.targetFPS;
  }

  public getAverageFrameTime(): number {
    if (this.frameTimeHistory.length === 0) {
      return this.targetFrameTime;
    }
    return (
      this.frameTimeHistory.reduce((sum, time) => sum + time, 0) /
      this.frameTimeHistory.length
    );
  }

  /**
   * Get performance stats
   */
  public getPerformanceStats(): PerformanceStats {
    return {
      fps: this.getFPS(),
      targetFPS: this.targetFPS,
      deltaTime: this.deltaTime,
      elapsedTime: this.elapsedTime,
      frameCount: this.frameCount,
      averageFrameTime: this.getAverageFrameTime(),
      isRunning: this.isRunning,
      isPaused: this.isPaused,
    };
  }
}

export interface PerformanceStats {
  fps: number;
  targetFPS: number;
  deltaTime: number;
  elapsedTime: number;
  frameCount: number;
  averageFrameTime: number;
  isRunning: boolean;
  isPaused: boolean;
}

// Singleton instance
let gameLoopInstance: GameLoop | null = null;

/**
 * Get the singleton GameLoop instance
 */
export function getGameLoop(): GameLoop {
  if (!gameLoopInstance) {
    gameLoopInstance = new GameLoop(60);
  }
  return gameLoopInstance;
}
